import { type Common } from '../common.js';
import {
  type FlowDefinitionState,
  type FlowDefinitionWithFlow,
} from '../models/flow/flowDefinition.js';
import {
  BlockRunDataVariant,
  type EntityFlowRun,
  type FindOneByIdQuery,
  type FlowRunBlockWithData,
  type FlowRunFindAllOptions,
  type FlowRunState,
  type FlowRunSummary,
  type FlowRunType,
  type FlowRunWithData,
} from '../models/flow/flowRun.js';
import {
  type FlowRunCostDataSource,
  type FlowRunCostResponse,
} from '../models/flow/flowRunCost.js';
import { isDefined } from '../utils.js';

export class API {
  common: Common;
  baseEndpoint = '/flow/flow-runs';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(queryParams?: FlowRunFindAllOptions): Promise<FlowRunSummary[]> {
    const url = this.common.constructPath(this.baseEndpoint, queryParams);
    return await this.common.getJSON(url);
  }

  async findAllExists(queryParams?: FlowRunFindAllOptions): Promise<{ exists: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/exists`, queryParams);
    return await this.common.getJSON(url);
  }

  async findOneById(flowRunId: string): Promise<{
    flowRun: FlowRunWithData;
    flowDefinition: FlowDefinitionWithFlow;
  }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowRunId}`, {
      dataVariant: BlockRunDataVariant.Redacted,
    });
    return await this.common.getJSON(url);
  }

  async findOneByIdWithQuery(props: { flowRunId: string; queryParams: FindOneByIdQuery }): Promise<{
    flowRun: FlowRunWithData;
    flowDefinition: FlowDefinitionWithFlow;
  }> {
    const { flowRunId, queryParams } = props;
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowRunId}`, queryParams);
    return await this.common.getJSON(url);
  }

  async findOneByIdBasic(flowRunId: string): Promise<{
    flowRun: {
      id: string;
      created: string;
      updated: string;
      state: FlowRunState;
      entityId: string | null;
      entityType: string | null;
      archivedAt: string | null;
    };
    flowDefinition: {
      id: string;
      version: number;
      state: FlowDefinitionState;
    };
    flow: {
      id: string;
      name: string;
    };
  }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowRunId}/basic`);
    return await this.common.getJSON(url);
  }

  async findInputsById(flowRunId: string): Promise<Record<string, unknown>> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowRunId}/inputs`);
    return await this.common.getJSON(url);
  }

  async findAllByEntity(entityId: string, entityType: string): Promise<EntityFlowRun[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/entity`, {
      entityId,
      entityType,
    });
    return await this.common.getJSON(url);
  }

  async getStats(options: {
    flowDefinitionId?: string;
    flowId?: string;
    agentId?: string;
    organizationId?: string;
    createdBefore?: Date | string;
    createdAfter?: Date | string;
    state?: FlowRunState | FlowRunState[];
    flowDefinitionState?: FlowDefinitionState | FlowDefinitionState[];
    flowRunType?: FlowRunType | FlowRunType[];
    version?: string | string[];
    groupId?: string;
    entityId?: string;
    startedByEmail?: string;
  }): Promise<{
    numUniquePatients: number;
    numUniqueEntities: { [k: string]: number };
    totalFlowRuns: number;
    failedFlowRuns: number;
    completedFlowRuns: number;
    canceledFlowRuns: number;
  }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/stats`, options);
    return await this.common.getJSON(url);
  }

  async getCost(params: {
    flowRunId: string;
    preferredDataSource?: FlowRunCostDataSource;
  }): Promise<FlowRunCostResponse> {
    const { flowRunId, preferredDataSource } = params;
    const url = this.common.constructPath(
      `${this.baseEndpoint}/${flowRunId}/cost`,
      isDefined(preferredDataSource) ? { preferredDataSource } : undefined
    );
    return await this.common.getJSON(url);
  }

  async getBlock(flowRunId: string, blockId: string): Promise<FlowRunBlockWithData> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${flowRunId}/blocks/${blockId}`);
    return await this.common.getJSON(url);
  }
}
